// Step 1 — choose the form (prototype `workRoot` + `propsForm`).

import { ChevronRight, FileText, Info, Lock } from 'lucide-react'
import { driverLabel, formOf } from './model'
import { opForm, useSetup } from './state'
import { HintBox, StateBox } from './ui'

export default function StepForm() {
  const { forms, formNo, setFormNo, draft, go } = useSetup()
  return (
    <>
      <div className="uds-lead">
        <h2>1. Choose the form</h2>
        <p>Additional attributes are captured against one form of the transaction. Pick the form, then decide
          which columns choose the attribute set, the value combinations, and the columns each combination asks for.</p>
      </div>
      <div className="uds-formcards">
        {forms.map(f => {
          const lf = formOf(draft.layer, f.formNo)
          const on = !!lf && lf.enabled !== false
          const parent = forms.find(x => x.formNo === f.parentFormNo)
          const cols = (lf?.sets || []).reduce((n, s) => n + (s.columns || []).length, 0)
          return (
            <button key={f.formNo} type="button" className={`uds-fc ${formNo === f.formNo ? 'on' : ''}`}
              onClick={() => setFormNo(f.formNo)}>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <FileText size={16} /><b>{f.title}</b><span className="uds-grow" />
                {on ? <span className="uds-pill uds-p-live">On</span> : <span className="uds-pill uds-p-std">Off</span>}
              </span>
              <span className="s">
                {f.isHeader ? 'Header form' : `Line form · child of ${parent?.title || 'the header'}`} · <span className="uds-mono">{f.table}</span>
              </span>
              <span className="s">
                {lf?.drivers?.length ? `Decided by ${lf.drivers.map(driverLabel).join(' + ')}` : 'Not decided yet'}
                {' · '}{(lf?.sets || []).length} combinations · {cols} columns
              </span>
            </button>
          )
        })}
      </div>
      <div>
        <button type="button" className="btn btn-primary" onClick={() => go({ t: 'decide' })}>
          Next: decided by <ChevronRight size={15} />
        </button>
      </div>
    </>
  )
}

export function FormPanel() {
  const { forms, formNo, draft, mode, additionsAllowed } = useSetup()
  const f = forms.find(x => x.formNo === formNo)
  const lf = formOf(draft.layer, formNo)
  const designForm = mode === 'tenant' && !!draft.design?.forms?.[formNo]
  const canCreate = !!lf || additionsAllowed(formNo)
  const locked = designForm || !canCreate
  const record = f?.isHeader ? 'record' : 'line'
  return (
    <>
      <div className="uds-props-h">
        <div className="row"><span className="uds-pill uds-p-acc">Form</span></div>
        <h2>{f?.title || `Form ${formNo}`}</h2>
        <div className="sub">{f?.table}</div>
      </div>
      <div className="uds-pbody">
        {designForm && (
          <HintBox icon={<Lock size={15} />} style={{ margin: '0 0 10px' }}>
            The application ships additional attributes on this form, so whether it captures them and its group heading
            are set in the transaction designer.
          </HintBox>
        )}
        {!lf && !canCreate && (
          <HintBox icon={<Lock size={15} />} style={{ margin: '0 0 10px' }}>
            The application does not allow organizations to add attributes to this form.
          </HintBox>
        )}
        <StateBox checked={!!lf && lf.enabled !== false} disabled={locked}
          onChange={(v) => draft.edit(opForm(formNo, fm => { fm.enabled = v }))}>
          Capture additional attributes on this form
        </StateBox>
        <label className="uds-lbl" htmlFor="uds-fgroup">Default group heading</label>
        <input id="uds-fgroup" className="uds-inp" value={lf?.group || ''} disabled={locked}
          placeholder="Additional details"
          onChange={e => { const v = e.target.value; draft.edit(opForm(formNo, fm => { fm.group = v })) }} />
        <div className="uds-hint">Used when an attribute set is shown inside the form instead of as its own group.</div>
        {mode === 'design' && (
          <>
            <div className="uds-lbl" style={{ marginTop: 16 }}>Organizations</div>
            <StateBox checked={lf?.allow_tenant_additions !== false}
              onChange={(v) => draft.edit(opForm(formNo, fm => { fm.allow_tenant_additions = v }))}>
              Let organizations add their own attribute sets and columns
            </StateBox>
            <div className="uds-hint">Off: they may only change what each standard set and column allows.</div>
          </>
        )}
        <HintBox icon={<Info size={15} />}>
          Values are saved with each {record} in the additional attributes store, in the same save as the
          {' '}{record}. The form&apos;s own table is not changed.
        </HintBox>
      </div>
    </>
  )
}
